/**
 * Pure Sichtbarkeits-Logik fürs Storefront-Ankündigungs-Banner. Frei von
 * React/IO → unit-testbar. Banner erscheint nur wenn: enabled + Nachricht
 * vorhanden + (falls gesetzt) innerhalb des Zeitfensters [starts_at, ends_at].
 */

import { fromZonedTime } from 'date-fns-tz';

export type AnnouncementStyle = 'info' | 'offer' | 'important';

export interface ResolvedAnnouncement {
  message: string;
  style: AnnouncementStyle;
  linkUrl: string | null;
  linkLabel: string | null;
}

export interface TenantAnnouncementFields {
  announcement_enabled?: boolean | null;
  announcement_message?: string | null;
  announcement_style?: string | null;
  announcement_link_url?: string | null;
  announcement_link_label?: string | null;
  announcement_starts_at?: string | null;
  announcement_ends_at?: string | null;
}

const STYLES: readonly AnnouncementStyle[] = ['info', 'offer', 'important'];

/** Naive datetime-local ("2026-08-04T23:59") in Tenant-TZ interpretieren; absolute
 *  Strings (mit Z/Offset) as-is. So driftet das Fenster nicht mit der Viewer-TZ. */
function toEpochMs(s: string, timeZone?: string): number | null {
  const isAbsolute = /[Zz]|[+-]\d{2}:?\d{2}$/.test(s);
  const d = !isAbsolute && timeZone ? fromZonedTime(s, timeZone) : new Date(s);
  const ms = d.getTime();
  return Number.isFinite(ms) ? ms : null;
}

/** Nur http(s) oder relative URLs — kein `javascript:`/`data:` aus Tenant-Input. */
function safeUrl(raw: string): string | null {
  const url = raw.trim();
  if (!url) return null;
  return /^https?:\/\//i.test(url) || url.startsWith('/') ? url : null;
}

export function resolveAnnouncement(
  t: TenantAnnouncementFields,
  nowMs: number,
  timeZone?: string,
): ResolvedAnnouncement | null {
  if (!t.announcement_enabled) return null;
  const message = (t.announcement_message ?? '').trim();
  if (!message) return null;

  if (t.announcement_starts_at) {
    const s = toEpochMs(t.announcement_starts_at, timeZone);
    if (s !== null && nowMs < s) return null; // noch nicht aktiv
  }
  if (t.announcement_ends_at) {
    const e = toEpochMs(t.announcement_ends_at, timeZone);
    if (e !== null && nowMs > e) return null; // abgelaufen
  }

  const style = STYLES.includes(t.announcement_style as AnnouncementStyle)
    ? (t.announcement_style as AnnouncementStyle)
    : 'info';
  const linkUrl = safeUrl(t.announcement_link_url ?? '');
  const linkLabel = linkUrl ? (t.announcement_link_label ?? '').trim() || null : null;

  return { message, style, linkUrl, linkLabel };
}

/** Stabiler Kurz-Hash der Nachricht → Dismiss-Key. Neue Nachricht ⇒ neuer Key
 *  ⇒ Banner erscheint wieder, gelesene bleiben weg. */
export function announcementHash(message: string, style: string): string {
  const s = `${style}:${message}`;
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return String(h >>> 0);
}
